import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/apiClient";
import { API_BASE } from "../api/config";
import { dragOutProps, contextShareProps } from "../utils/dragOut";
import "./KomprimierungPage.css";

const BASE = `${API_BASE}/zuordnen/api/bilder-komprimierung`;

export default function KomprimierungPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Modus aus URL: ?modus=zaehler|bilder oder Pfad /zuordnen/bilder-komprimierung
  const modus = (() => {
    const param = new URLSearchParams(location.search).get("modus");
    if (param === "zaehler" || param === "bilder") return param;
    if (location.pathname.includes("bilder-komprimierung")) return "bilder";
    return "zaehler";
  })();
  const modusLabel = modus === "bilder" ? "Bilder" : "Zähler";

  // Helper: fügt modus zu params-Object hinzu
  const withModus = (params = {}) => ({ ...params, modus });

  const [currentPath, setCurrentPath] = useState("");
  const [browseData, setBrowseData] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);

  // Rekursiver Scan
  const [scanResult, setScanResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);

  // Global-Status
  const [status, setStatus] = useState(null);

  // Ausführung
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Confirm-Modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmRekursiv, setConfirmRekursiv] = useState(true);

  // Lösch-Modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // { pfade, isFolder, ordnerName, hatInhalt }

  // Umbenenn-Warnung
  const [showUmbenennWarning, setShowUmbenennWarning] = useState(null);

  // Auswahl-Korb: EINZIGE Quelle der Wahrheit für markierte Dateien.
  // Map<dropbox_pfad, dateiObj> — überlebt Ordner-Navigation, damit man
  // Dateien aus komplett unterschiedlichen Pfaden zusammen sammeln kann.
  const [korb, setKorb] = useState(new Map());

  const korbSync = useCallback((files, add) => {
    setKorb(prev => {
      const next = new Map(prev);
      files.forEach(f => {
        if (add) next.set(f.dropbox_pfad, f);
        else next.delete(f.dropbox_pfad);
      });
      return next;
    });
  }, []);
  const korbToggle = (d) => korbSync([d], !korb.has(d.dropbox_pfad));
  const korbRemovePaths = useCallback((pfade) => {
    setKorb(prev => {
      const next = new Map(prev);
      pfade.forEach(p => next.delete(p));
      return next;
    });
  }, []);
  const korbClear = () => setKorb(new Map());

  // Auswahl im aktuellen Ordner (abgeleitet aus dem Korb)
  const selectedFiles = React.useMemo(() => {
    const s = new Set();
    (browseData?.dateien || []).forEach(d => { if (korb.has(d.dropbox_pfad)) s.add(d.dropbox_pfad); });
    return s;
  }, [browseData, korb]);

  // Ordner-Expand: Map<pfad, { loading, data }>
  const [expandedFolders, setExpandedFolders] = useState({});

  // Datei-Vorschau
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSynologyPfad, setPreviewSynologyPfad] = useState(null);
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [originalUrl, setOriginalUrl] = useState(null);
  const [originalLoading, setOriginalLoading] = useState(false);
  const [viewerPos, setViewerPos] = useState(() => ({ x: Math.max(20, window.innerWidth - 440), y: 80 }));
  const [zoom, setZoom] = useState(1);
  const isDragging = useRef(false);
  const folderStatusReqId = useRef(0);
  const dragOffset = useRef({ x: 0, y: 0 });

  // OCR-Ordner-Batch
  const [ocrProgress, setOcrProgress] = useState(null); // { queued, total, already_cached }
  const ocrPollingRef = useRef(null);

  // Komprimierungs-Job (persistent über Navigation via localStorage)
  const JOB_KEY = `komprimier_job_${modus}`;
  const [komprimierJob, setKomprimierJob] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`komprimier_job_${modus}`)) || null; }
    catch { return null; }
  });
  const jobPollingRef = useRef(null);

  const _setJob = (job) => {
    setKomprimierJob(job);
    if (job) localStorage.setItem(JOB_KEY, JSON.stringify(job));
    else localStorage.removeItem(JOB_KEY);
  };

  // Polling für laufende Jobs
  useEffect(() => {
    if (!komprimierJob || komprimierJob.status !== "running") return;
    if (jobPollingRef.current) clearInterval(jobPollingRef.current);
    jobPollingRef.current = setInterval(async () => {
      try {
        const r = await api.get(`${BASE}/job-status`, { params: { job_id: komprimierJob.job_id } });
        const s = r.data;
        if (s.status === "unknown") {
          // Server wurde neu gestartet, Job nicht mehr bekannt
          clearInterval(jobPollingRef.current);
          jobPollingRef.current = null;
          _setJob(null);
          return;
        }
        _setJob({ ...komprimierJob, ...s });
        if (s.status === "done" || s.status === "error") {
          clearInterval(jobPollingRef.current);
          jobPollingRef.current = null;
          setRunning(false);
          loadStatus();
          loadFolder(currentPath, true);
          // Job noch 8s anzeigen dann ausblenden
          setTimeout(() => _setJob(null), 8000);
        }
      } catch (_) {}
    }, 2000);
    return () => { if (jobPollingRef.current) clearInterval(jobPollingRef.current); };
  }, [komprimierJob?.job_id, komprimierJob?.status]);

  // Ordner-Farbstatus (sofort aus Cache, dann Hintergrund-Refresh)
  const [folderStatus, setFolderStatus] = useState({});

  const loadStatus = () => {
    api.get(`${BASE}/status`, { params: withModus() }).then(r => setStatus(r.data)).catch(() => {});
  };

  const loadFolder = useCallback(async (path, keepExpanded = false) => {
    setBrowseLoading(true);
    setResult(null);
    setScanResult(null);
    if (!keepExpanded) setExpandedFolders({});
    try {
      const r = await api.get(`${BASE}/browse`, { params: withModus({ pfad: path }) });
      setBrowseData(r.data);
      // Sofort gecachte Farben aus Browse-Daten setzen
      if (r.data.ordner && r.data.ordner.length > 0) {
        const cached = {};
        r.data.ordner.forEach(o => { if (o.agg_status) cached[o.pfad] = o.agg_status; });
        setFolderStatus(cached);
        // Im Hintergrund frische Farben laden und Cache aktualisieren
        const reqId = ++folderStatusReqId.current;
        api.get(`${BASE}/browse/folder-status`, { params: withModus({ pfad: path }) })
          .then(sr => { if (reqId === folderStatusReqId.current) setFolderStatus(sr.data.status || {}); })
          .catch(() => {});
      } else {
        setFolderStatus({});
      }
    } catch (e) {
      console.error("Browse:", e);
    } finally {
      setBrowseLoading(false);
    }
  }, []);

  const restoredRef = useRef(false);

  useEffect(() => {
    // State von vorheriger Session wiederherstellen (Zurück von BilderVerschiebenPage)
    const saved = sessionStorage.getItem(`komprimierung_state_${modus}`);
    if (saved && !restoredRef.current) {
      restoredRef.current = true;
      sessionStorage.removeItem(`komprimierung_state_${modus}`);
      try {
        const s = JSON.parse(saved);
        const path = s.currentPath || "";
        setCurrentPath(path);
        // Frische Daten laden statt alte browseData zu verwenden
        loadFolder(path, true);
        // Expandierte Ordner neu laden (frisch, aber gleiche Ordner offen)
        const expandKeys = Object.keys(s.expandedFolders || {});
        if (expandKeys.length > 0) {
          (async () => {
            const ef = {};
            for (const key of expandKeys) {
              try {
                const r = await api.get(`${BASE}/browse-rekursiv`, { params: withModus({ pfad: key }) });
                ef[key] = { loading: false, data: r.data };
              } catch (_) {}
            }
            setExpandedFolders(ef);
          })();
        }
        return;
      } catch (e) {
        console.warn("State-Restore fehlgeschlagen:", e);
      }
    }
    if (!restoredRef.current) {
      loadStatus();
      loadFolder("");
    }
  }, [loadFolder]);

  const navigateTo = (path) => {
    setCurrentPath(path);
    loadFolder(path, false);
  };

  // Phase 7: Zurück-Navigation mit Ordner-State beibehalten
  const navigateBack = () => {
    if (!currentPath) {
      navigate("/");
      return;
    }
    const parts = currentPath.split("/");
    parts.pop();
    const parentPath = parts.join("/");
    setCurrentPath(parentPath);
    loadFolder(parentPath, true);
  };

  const handleScanRekursiv = async () => {
    setScanLoading(true);
    setScanResult(null);
    try {
      const r = await api.get(`${BASE}/scan`, { params: withModus({ ordner: currentPath }) });
      setScanResult(r.data);
    } catch (e) {
      alert("Scan fehlgeschlagen: " + (e.message || e));
    } finally {
      setScanLoading(false);
    }
  };

  const handleKomprimieren = async (rekursiv = true) => {
    setConfirmRekursiv(rekursiv);
    setShowConfirm(true);
  };

  const doKomprimieren = async () => {
    setShowConfirm(false);
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post(`${BASE}/ausfuehren`, {
        ordner: currentPath,
        rekursiv: confirmRekursiv,
        modus,
      });
      _setJob({ job_id: r.data.job_id, status: "running", total: r.data.total,
                done: 0, current_file: "Starte…", verarbeitet: 0,
                uebersprungen: 0, fehler: 0, gespart_mb: 0 });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || String(e);
      alert("Fehler: " + detail);
      setRunning(false);
    }
  };

  const toggleFile = (d) => korbToggle(d);

  const toggleAll = () => {
    if (!browseData) return;
    const alleSelektiert = selectedFiles.size >= browseData.dateien.length;
    korbSync(browseData.dateien, !alleSelektiert);
  };

  const handleKomprimierenAuswahl = async () => {
    if (selectedFiles.size === 0) return;
    const selUebersprungen = browseData?.dateien?.filter(d => selectedFiles.has(d.dropbox_pfad) && d.status === "uebersprungen") || [];
    const selKomprimierbar = browseData?.dateien?.filter(d => selectedFiles.has(d.dropbox_pfad) && d.status === "komprimierbar") || [];
    if (selUebersprungen.length > 0) {
      setShowUmbenennWarning({
        count: selUebersprungen.length,
        komprimierbareCount: selKomprimierbar.length,
        onConfirm: () => doKomprimierenAuswahl(selKomprimierbar.map(d => d.dropbox_pfad)),
      });
      return;
    }
    doKomprimierenAuswahl(selKomprimierbar.map(d => d.dropbox_pfad));
  };

  const doKomprimierenAuswahl = async (dateien) => {
    setShowUmbenennWarning(null);
    if (dateien.length === 0) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post(`${BASE}/ausfuehren-einzeln`, { dateien, modus });
      _setJob({ job_id: r.data.job_id, status: "running", total: r.data.total,
                done: 0, current_file: "Starte…", verarbeitet: 0,
                uebersprungen: 0, fehler: 0, gespart_mb: 0 });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || String(e);
      alert("Fehler: " + detail);
      setRunning(false);
    }
  };

  // --- Ordner-Expand/Collapse ---
  const toggleFolderExpand = async (ordnerPfad) => {
    if (expandedFolders[ordnerPfad]) {
      // Collapse
      setExpandedFolders(prev => {
        const next = { ...prev };
        delete next[ordnerPfad];
        return next;
      });
      return;
    }
    // Expand: load data
    setExpandedFolders(prev => ({
      ...prev,
      [ordnerPfad]: { loading: true, data: null },
    }));
    try {
      const r = await api.get(`${BASE}/browse-rekursiv`, { params: withModus({ pfad: ordnerPfad }) });
      setExpandedFolders(prev => ({
        ...prev,
        [ordnerPfad]: { loading: false, data: r.data },
      }));
    } catch (e) {
      console.error("Browse-Rekursiv Fehler:", e);
      setExpandedFolders(prev => {
        const next = { ...prev };
        delete next[ordnerPfad];
        return next;
      });
    }
  };

  const toggleExpandedFile = (ordnerPfad, d) => korbToggle(d);

  const toggleExpandedAll = (ordnerPfad, statusFilter) => {
    const folder = expandedFolders[ordnerPfad];
    if (!folder?.data) return;
    const files = Object.values(folder.data.gruppen).flat().filter(d => d.status === statusFilter);
    const allSelected = files.every(d => korb.has(d.dropbox_pfad));
    korbSync(files, !allSelected);
  };

  const handleExpandedKomprimieren = async (ordnerPfad) => {
    const folder = expandedFolders[ordnerPfad];
    if (!folder?.data) return;
    const allFiles = Object.values(folder.data.gruppen).flat();
    const sel = allFiles.filter(d => korb.has(d.dropbox_pfad));
    if (sel.length === 0) return;
    const komprimierbar = sel.filter(d => d.status === "komprimierbar").map(d => d.dropbox_pfad);
    const uebersprungen = sel.filter(d => d.status === "uebersprungen").map(d => d.dropbox_pfad);
    if (uebersprungen.length > 0) {
      setShowUmbenennWarning({
        count: uebersprungen.length,
        komprimierbareCount: komprimierbar.length,
        onConfirm: () => doExpandedKomprimieren(ordnerPfad, komprimierbar),
      });
      return;
    }
    if (komprimierbar.length === 0) return;
    doExpandedKomprimieren(ordnerPfad, komprimierbar);
  };

  const doExpandedKomprimieren = async (ordnerPfad, dateien) => {
    setShowUmbenennWarning(null);
    if (dateien.length === 0) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post(`${BASE}/ausfuehren-einzeln`, { dateien, modus });
      setResult(r.data);
      loadStatus();
      // Ordner-Farben werden automatisch über loadFolder aktualisiert
      loadFolder(currentPath, true);
      toggleFolderExpand(ordnerPfad);
      setTimeout(() => toggleFolderExpand(ordnerPfad), 300);
    } catch (e) {
      alert("Fehler: " + (e?.response?.data?.detail || e.message || String(e)));
    } finally {
      setRunning(false);
    }
  };

  // Lösch-Funktionen
  const handleDeleteFiles = async (pfade) => {
    if (pfade.length === 0) return;
    setShowDeleteConfirm({ pfade, isFolder: false, hatInhalt: false });
  };

  const handleDeleteFolder = async (ordnerPfad, ordnerName) => {
    // Erst Inhalt prüfen
    try {
      const r = await api.get(`${BASE}/ordner-inhalt-check`, { params: withModus({ pfad: ordnerPfad }) });
      setShowDeleteConfirm({ pfade: [ordnerPfad], isFolder: true, ordnerName, hatInhalt: r.data.hat_inhalt, anzahlDateien: r.data.anzahl_dateien });
    } catch (e) {
      alert("Fehler beim Prüfen: " + (e?.response?.data?.detail || e.message));
    }
  };

  const doLoeschen = async () => {
    if (!showDeleteConfirm) return;
    const { pfade } = showDeleteConfirm;
    setShowDeleteConfirm(null);
    try {
      const r = await api.post(`${BASE}/loeschen`, { pfade });
      if (r.data.fehler?.length > 0) {
        alert("Teils fehlgeschlagen:\n" + r.data.fehler.map(f => f.pfad + ": " + f.fehler).join("\n"));
      }
      loadFolder(currentPath, true);
      korbRemovePaths(pfade);
    } catch (e) {
      alert("Löschen fehlgeschlagen: " + (e?.response?.data?.detail || e.message));
    }
  };

  // State in sessionStorage speichern bevor wir zu BilderVerschiebenPage navigieren
  const saveStateBeforeNavigate = useCallback(() => {
    const ef = {};
    for (const k of Object.keys(expandedFolders)) {
      ef[k] = {};
    }
    sessionStorage.setItem(`komprimierung_state_${modus}`, JSON.stringify({
      currentPath,
      expandedFolders: ef,
    }));
  }, [currentPath, expandedFolders]);

  const handleExpandedRename = (ordnerPfad) => {
    // Wenn ordnerPfad angegeben: nur dieser Ordner. Sonst: nur expandierte MIT Auswahl.
    const ordnerKeys = ordnerPfad
      ? [ordnerPfad]
      : Object.keys(expandedFolders).filter(k => {
          const fdata = expandedFolders[k]?.data;
          if (!fdata) return false;
          return Object.values(fdata.gruppen).flat().some(d => korb.has(d.dropbox_pfad));
        });
    const alleDateien = [];
    let ersterOrdner = null;
    for (const key of ordnerKeys) {
      const folder = expandedFolders[key];
      if (!folder?.data) continue;
      if (!ersterOrdner) ersterOrdner = folder.data.full_path;
      const allFiles = Object.values(folder.data.gruppen).flat();
      const ausgewaehlt = allFiles.filter(d => korb.has(d.dropbox_pfad));
      const dateien = ausgewaehlt.length > 0 ? ausgewaehlt : allFiles;
      alleDateien.push(...dateien);
    }
    if (alleDateien.length === 0) return;
    saveStateBeforeNavigate();
    // Bilder-Modus → FotosVerarbeitenPage; Zähler-Modus → BilderVerschiebenPage
    const targetPath = modus === "bilder" ? "/zuordnen/fotos" : "/zuordnen/bilder";
    navigate(targetPath, {
      state: {
        ordner: ersterOrdner,
        modus: "umbenennen",
        autoAnalyse: true,
        nurDateien: alleDateien.map(d => d.dropbox_pfad),
        returnTo: location.pathname,
      },
    });
  };

  const handleAllExpandedKomprimieren = () => {
    // Alle komprimierbaren Dateien aus allen expandierten Ordnern (mit Auswahl bevorzugt)
    const ordnerKeys = Object.keys(expandedFolders);
    const alleDateien = [];
    for (const key of ordnerKeys) {
      const folder = expandedFolders[key];
      if (!folder?.data) continue;
      const allFiles = Object.values(folder.data.gruppen).flat();
      const komprimierbar = allFiles.filter(d => d.status === "komprimierbar");
      const selKompr = komprimierbar.filter(d => korb.has(d.dropbox_pfad));
      // Wenn Auswahl vorhanden: nur ausgewählte; sonst alle komprimierbaren
      alleDateien.push(...(selKompr.length > 0 ? selKompr : komprimierbar));
    }
    if (alleDateien.length === 0) return;
    doKomprimierenAuswahl(alleDateien.map(d => d.dropbox_pfad));
  };

  // ── Auswahl-Korb: Aktionen über ALLE gesammelten Dateien (ordnerübergreifend) ──
  const korbOrdner = React.useMemo(
    () => new Set([...korb.keys()].map(p => p.substring(0, p.lastIndexOf("/")))),
    [korb]
  );
  const korbKomprimierbar = React.useMemo(
    () => [...korb.values()].filter(d => d.status === "komprimierbar"),
    [korb]
  );

  // Gemeinsamer Eltern-Pfad aller Korb-Dateien (nur als Ordner-Hint fürs Handoff)
  const _commonParent = (pfade) => {
    if (pfade.length === 0) return "";
    let parts = pfade[0].substring(0, pfade[0].lastIndexOf("/")).split("/");
    for (const p of pfade.slice(1)) {
      const pp = p.substring(0, p.lastIndexOf("/")).split("/");
      let i = 0;
      while (i < parts.length && i < pp.length && parts[i] === pp[i]) i++;
      parts = parts.slice(0, i);
    }
    return parts.join("/");
  };

  // Umbenennen + Komprimieren: Handoff der Korb-Dateien in die Fotos-/Bilder-
  // Umbenennen-Ansicht. Jede Datei behält dort ihren eigenen Quell-Ordner;
  // komprimiert wird automatisch durch die Job-Pipeline (Welle 28).
  const handleKorbRename = () => {
    const pfade = [...korb.keys()];
    if (pfade.length === 0) return;
    saveStateBeforeNavigate();
    const targetPath = modus === "bilder" ? "/zuordnen/fotos" : "/zuordnen/bilder";
    navigate(targetPath, {
      state: {
        ordner: _commonParent(pfade) || browseData?.full_path || "",
        modus: "umbenennen",
        autoAnalyse: true,
        nurDateien: pfade,
        returnTo: location.pathname,
      },
    });
  };

  const handleKorbKomprimieren = () => {
    const entries = [...korb.values()];
    const uebersprungen = entries.filter(d => d.status === "uebersprungen");
    const komprimierbar = korbKomprimierbar;
    if (komprimierbar.length === 0) return;
    if (uebersprungen.length > 0) {
      setShowUmbenennWarning({
        count: uebersprungen.length,
        komprimierbareCount: komprimierbar.length,
        onConfirm: () => doKomprimierenAuswahl(komprimierbar.map(d => d.dropbox_pfad)),
      });
      return;
    }
    doKomprimierenAuswahl(komprimierbar.map(d => d.dropbox_pfad));
  };

  // Drag handlers
  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      setViewerPos({
        x: Math.max(0, Math.min(window.innerWidth - 60, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 60, e.clientY - dragOffset.current.y)),
      });
    };
    const onUp = () => { isDragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const onViewerDragStart = (e) => {
    isDragging.current = true;
    dragOffset.current = { x: e.clientX - viewerPos.x, y: e.clientY - viewerPos.y };
    e.preventDefault();
  };

  const onViewerWheel = (e) => {
    e.preventDefault();
    setZoom(z => Math.min(5, Math.max(0.3, z - e.deltaY * 0.001)));
  };

  const openPreview = async (d) => {
    setPreviewName(d.name);
    setPreviewUrl(null);
    setZoom(1);
    setShowingOriginal(false);
    setOriginalUrl(null);
    setPreviewSynologyPfad(d.synology_pfad || null);
    setPreviewLoading(true);
    try {
      const r = await api.get(`${API_BASE}/zuordnen/api/bilder/thumbnail`, {
        params: { path: d.dropbox_pfad, max_size: 1200 },
      });
      setPreviewUrl(`data:image/jpeg;base64,${r.data.image}`);
    } catch (e) {
      console.error("Preview-Fehler:", e);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openOriginal = async () => {
    if (!previewSynologyPfad) return;
    setShowingOriginal(true);
    // Original bereits geladen → nur einblenden, kein erneuter Download
    if (originalUrl) return;
    setOriginalLoading(true);
    try {
      const r = await api.get(`${BASE}/original-thumbnail`, {
        params: { pfad: previewSynologyPfad, max_size: 1200 },
      });
      setOriginalUrl(`data:image/jpeg;base64,${r.data.image}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || String(e);
      const isConnection = msg.includes("Connection refused") || msg.includes("connect");
      alert(isConnection
        ? "Synology NAS nicht erreichbar – SFTP-Verbindung fehlgeschlagen.\nBitte prüfen ob SSH/SFTP auf der Synology aktiv ist und Port 22 freigegeben wurde."
        : "Original konnte nicht geladen werden: " + msg
      );
      setShowingOriginal(false);
    } finally {
      setOriginalLoading(false);
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1_048_576) return (bytes / 1024).toFixed(0) + " KB";
    return (bytes / 1_048_576).toFixed(1) + " MB";
  };

  // OCR-Ordner-Batch starten
  const handleOcrOrdner = async (ordnerPfad) => {
    try {
      const r = await api.post(`${API_BASE}/zuordnen/api/bilder/ocr-ordner`, null, {
        params: { ordner: ordnerPfad || currentPath },
      });
      const data = r.data;
      if (data.queued === 0) {
        setOcrProgress({ queued: 0, total: data.total, already_cached: data.already_cached, done: true });
        setTimeout(() => setOcrProgress(null), 4000);
        return;
      }
      setOcrProgress({ queued: data.queued, total: data.total, already_cached: data.already_cached, done: false, current: 0 });
      // Polling starten
      if (ocrPollingRef.current) clearInterval(ocrPollingRef.current);
      ocrPollingRef.current = setInterval(async () => {
        try {
          const s = await api.get(`${API_BASE}/zuordnen/api/bilder/ocr-status`);
          const st = s.data;
          const remaining = (st.queue_priority || 0) + (st.queue_batch || 0);
          const processed = data.queued - remaining;
          setOcrProgress(prev => prev ? { ...prev, current: Math.max(0, processed), done: remaining === 0 && st.state === "idle" } : null);
          if (remaining === 0 && st.state === "idle") {
            clearInterval(ocrPollingRef.current);
            ocrPollingRef.current = null;
            setTimeout(() => setOcrProgress(null), 5000);
          }
        } catch (_) {}
      }, 3000);
    } catch (e) {
      alert("OCR-Ordner Fehler: " + (e?.response?.data?.detail || e.message || String(e)));
    }
  };

  // Cleanup OCR-Polling bei Unmount
  useEffect(() => {
    return () => {
      if (ocrPollingRef.current) clearInterval(ocrPollingRef.current);
    };
  }, []);

  // Breadcrumbs
  const breadcrumbs = currentPath ? currentPath.split("/") : [];

  // Stats: aus scanResult (rekursiv) oder browseData (aktueller Ordner)
  const komprimierbarerCount = scanResult?.gesamt_komprimierbar ?? browseData?.stats?.komprimierbar ?? 0;

  return (
    <div className="kompr-page">
      {/* Header */}
      <div className="kompr-header">
        <button className="kompr-back" onClick={() => {
          if (currentPath === "") {
            navigate("/");
          } else {
            navigateBack();
          }
        }}>← Zurück</button>
        <h2>🗜️ {modusLabel} Bilder verkleinern</h2>
      </div>

      {/* Status-Bar */}
      {status && (
        <div className="kompr-status-bar">
          <span className="kompr-stat">
            <strong>{status.komprimiert}</strong> komprimiert
          </span>
          <span className="kompr-stat">
            <strong>{status.gespart_mb} MB</strong> gespart
          </span>
          <span className="kompr-stat">
            <strong>{status.synology_kopiert}</strong> auf NAS
          </span>
          {status.fehler_count > 0 && (
            <span className="kompr-stat kompr-stat--error">
              <strong>{status.fehler_count}</strong> Fehler
            </span>
          )}
        </div>
      )}

      {/* Komprimierung Fortschrittsleiste */}
      {komprimierJob && (
        <div className={`kompr-job-bar ${komprimierJob.status === "done" ? "kompr-job-bar--done" : komprimierJob.status === "error" ? "kompr-job-bar--error" : "kompr-job-bar--running"}`}>
          <div className="kompr-job-bar-top">
            <span className="kompr-job-label">
              {komprimierJob.status === "done"
                ? `✓ Fertig: ${komprimierJob.verarbeitet} komprimiert, ${komprimierJob.gespart_mb} MB gespart`
                : komprimierJob.status === "error"
                ? "✗ Fehler beim Komprimieren"
                : komprimierJob.total > 0
                ? `Komprimierung läuft: ${komprimierJob.done} / ${komprimierJob.total}`
                : "Komprimierung läuft: Dateien werden gezählt…"}
            </span>
            {komprimierJob.fehler > 0 && (
              <span className="kompr-job-errors">⚠ {komprimierJob.fehler} Fehler</span>
            )}
            {komprimierJob.uebersprungen > 0 && (
              <span className="kompr-job-skipped">{komprimierJob.uebersprungen} übersprungen</span>
            )}
            {komprimierJob.status !== "done" && komprimierJob.status !== "error" && (
              <button className="kompr-job-dismiss" onClick={() => _setJob(null)} title="Ausblenden (Job läuft weiter)">×</button>
            )}
          </div>
          {komprimierJob.total > 0 && (
            <div className="kompr-job-progress-track">
              <div
                className="kompr-job-progress-fill"
                style={{ width: `${Math.round((komprimierJob.done / komprimierJob.total) * 100)}%` }}
              />
            </div>
          )}
          {komprimierJob.current_file && komprimierJob.status === "running" && (
            <div className="kompr-job-current">
              {komprimierJob.current_file}
              {komprimierJob.current_phase && (
                <span className="kompr-job-phase"> — {komprimierJob.current_phase}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Breadcrumb */}
      <div className="kompr-breadcrumb">
        <span
          className={`kompr-crumb ${currentPath === "" ? "kompr-crumb--active" : ""}`}
          onClick={() => navigateTo("")}
        >
          📁 {modusLabel}
        </span>
        {breadcrumbs.map((part, i) => (
          <React.Fragment key={i}>
            <span className="kompr-crumb-sep">/</span>
            <span
              className={`kompr-crumb ${i === breadcrumbs.length - 1 ? "kompr-crumb--active" : ""}`}
              onClick={() => navigateTo(breadcrumbs.slice(0, i + 1).join("/"))}
            >
              {part}
            </span>
          </React.Fragment>
        ))}
        <button
          className="kompr-btn kompr-btn--ocr"
          onClick={() => handleOcrOrdner(currentPath)}
          disabled={!!ocrProgress && !ocrProgress.done}
          title="Alle Bilder in diesem Ordner (inkl. Unterordner) durch OCR analysieren"
          style={{ marginLeft: "auto", fontSize: "0.85em", padding: "4px 12px" }}
        >
          🔍 OCR Ordner
        </button>
      </div>

      {/* OCR-Fortschritt */}
      {ocrProgress && (
        <div className="kompr-ocr-progress" style={{ padding: "8px 16px", background: ocrProgress.done ? "#1a3a1a" : "#1a2a3a", borderRadius: 8, margin: "8px 0", display: "flex", alignItems: "center", gap: 12 }}>
          {ocrProgress.queued === 0 ? (
            <span>✅ Alle {ocrProgress.total} Bilder bereits im OCR-Cache</span>
          ) : ocrProgress.done ? (
            <span>✅ OCR abgeschlossen: {ocrProgress.queued} Bilder verarbeitet</span>
          ) : (
            <>
              <span>⏳ OCR: {ocrProgress.current || 0} / {ocrProgress.queued}</span>
              <div style={{ flex: 1, height: 6, background: "#333", borderRadius: 3, overflow: "hidden" }}>
                <div style={{
                  width: `${Math.round(((ocrProgress.current || 0) / ocrProgress.queued) * 100)}%`,
                  height: "100%", background: "#4a9eff", borderRadius: 3, transition: "width 0.3s",
                }} />
              </div>
              <span style={{ fontSize: "0.85em", opacity: 0.7 }}>({ocrProgress.already_cached} bereits gecacht)</span>
            </>
          )}
        </div>
      )}

      {/* Loading */}
      {browseLoading && <div className="kompr-loading">Lade Ordner…</div>}

      {/* Folder Content */}
      {!browseLoading && browseData && (
        <>
          {/* Stats für aktuellen Ordner */}
          {browseData.stats.bilder > 0 && (
            <div className="kompr-folder-stats">
              <div className="kompr-fs-item">
                <span className="kompr-fs-val">{browseData.stats.bilder}</span>
                <span className="kompr-fs-label">Bilder</span>
              </div>
              <div className="kompr-fs-item">
                <span className="kompr-fs-val">{fmtSize(browseData.stats.groesse)}</span>
                <span className="kompr-fs-label">Gesamt</span>
              </div>
              <div className="kompr-fs-item kompr-fs-item--primary">
                <span className="kompr-fs-val">{browseData.stats.komprimierbar}</span>
                <span className="kompr-fs-label">komprimierbar</span>
              </div>
              {browseData.stats.ersparnis_schaetzung > 0 && (
                <div className="kompr-fs-item kompr-fs-item--savings">
                  <span className="kompr-fs-val">~{fmtSize(browseData.stats.ersparnis_schaetzung)}</span>
                  <span className="kompr-fs-label">Ersparnis</span>
                </div>
              )}
              <div className="kompr-fs-item kompr-fs-item--done">
                <span className="kompr-fs-val">{browseData.stats.erledigt}</span>
                <span className="kompr-fs-label">erledigt</span>
              </div>
            </div>
          )}

          {/* Unterordner */}
          {browseData.ordner.length > 0 && (
            <div className="kompr-folders">
              <div className="kompr-section-label" style={{ display: "flex", alignItems: "center" }}>
                Ordner
                {Object.keys(expandedFolders).length > 0 && (() => {
                  const totalSelected = Object.values(expandedFolders).reduce((sum, fd) => {
                    if (!fd.data) return sum;
                    return sum + Object.values(fd.data.gruppen).flat().filter(d => korb.has(d.dropbox_pfad)).length;
                  }, 0);
                  const totalKomprimierbar = Object.values(expandedFolders).reduce((sum, fd) => {
                    if (!fd.data) return sum;
                    const allFiles = Object.values(fd.data.gruppen).flat();
                    const selKompr = allFiles.filter(d => d.status === "komprimierbar" && korb.has(d.dropbox_pfad)).length;
                    const allKompr = allFiles.filter(d => d.status === "komprimierbar").length;
                    return sum + (selKompr > 0 ? selKompr : allKompr);
                  }, 0);
                  return (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      {totalKomprimierbar > 0 && (
                        <button
                          className="kompr-btn kompr-btn--start kompr-btn--sm"
                          onClick={handleAllExpandedKomprimieren}
                          disabled={running}
                        >
                          {running ? "⏳" : `🗜️ ${totalKomprimierbar} komprimieren (alle Ordner)`}
                        </button>
                      )}
                      {totalSelected > 0 && (
                        <button
                          className="kompr-btn kompr-btn--rename kompr-btn--sm"
                          onClick={() => handleExpandedRename(null)}
                        >
                          📝 {totalSelected} umbenennen (alle Ordner)
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
              {browseData.ordner.map((o) => (
                <React.Fragment key={o.pfad}>
                  <div className={`kompr-folder-item ${expandedFolders[o.pfad] ? "kompr-folder-item--expanded" : ""}`}>
                    <input
                      type="checkbox"
                      className="kompr-checkbox kompr-folder-checkbox"
                      checked={!!expandedFolders[o.pfad]}
                      onChange={() => toggleFolderExpand(o.pfad)}
                      onClick={e => e.stopPropagation()}
                    />
                    <span className="kompr-folder-icon">📁</span>
                    <span className={`kompr-folder-name ${folderStatus[o.pfad] ? "kompr-folder-name--" + folderStatus[o.pfad] : ""}`} onClick={() => navigateTo(o.pfad)}>{o.name}</span>
                    <span className="kompr-folder-arrow" onClick={() => navigateTo(o.pfad)}>›</span>
                    <button
                      className="kompr-folder-delete-btn"
                      title="Ordner löschen"
                      onClick={e => { e.stopPropagation(); handleDeleteFolder(o.pfad, o.name); }}
                    >🗑️</button>
                  </div>

                  {/* Expandierte Ordner-Ansicht */}
                  {expandedFolders[o.pfad] && (
                    <div className="kompr-folder-expand">
                      {expandedFolders[o.pfad].loading ? (
                        <div className="kompr-expand-loading">⏳ Lade Dateien…</div>
                      ) : expandedFolders[o.pfad].data ? (() => {
                        const fd = expandedFolders[o.pfad];
                        const allFiles = Object.values(fd.data.gruppen).flat();
                        const fdSel = allFiles.filter(d => korb.has(d.dropbox_pfad));
                        const komprimierbare = allFiles.filter(d => d.status === "komprimierbar");
                        const umbenennbare = allFiles.filter(d => d.status === "uebersprungen");
                        const erledigte = allFiles.filter(d => d.status === "erledigt");
                        const originale = allFiles.filter(d => d.status === "original");
                        const appleSkip = allFiles.filter(d => d.status === "apple_skip");
                        const selKompr = komprimierbare.filter(d => korb.has(d.dropbox_pfad)).length;
                        const selUmb = umbenennbare.filter(d => korb.has(d.dropbox_pfad)).length;
                        const selErl = erledigte.filter(d => korb.has(d.dropbox_pfad)).length;
                        const selOrig = originale.filter(d => korb.has(d.dropbox_pfad)).length;

                        // Dateien nach Ordner gruppieren für eine Sektion
                        const renderSection = (files, statusKey) => {
                          const byDir = {};
                          files.forEach(f => {
                            const dir = Object.entries(fd.data.gruppen).find(([, arr]) => arr.includes(f))?.[0] || ".";
                            if (!byDir[dir]) byDir[dir] = [];
                            byDir[dir].push(f);
                          });
                          const dirs = Object.keys(byDir).sort();
                          return dirs.map(dir => (
                            <React.Fragment key={dir}>
                              {dir !== "." && <div className="kompr-expand-subdir" onClick={() => {
                                const dirFiles = byDir[dir];
                                const allSel = dirFiles.every(f => korb.has(f.dropbox_pfad));
                                korbSync(dirFiles, !allSel);
                              }} style={{ cursor: "pointer" }}>📂 {dir}</div>}
                              {byDir[dir].map(d => (
                                <div
                                  key={d.dropbox_pfad}
                                  className={`kompr-expand-file kompr-expand-file--${d.status} ${d.status !== "apple_skip" && korb.has(d.dropbox_pfad) ? "kompr-expand-file--selected" : ""}`}
                                  onClick={d.status !== "apple_skip" ? () => toggleExpandedFile(o.pfad, d) : undefined}
                                >
                                  {d.status !== "apple_skip" && (
                                    <input type="checkbox" className="kompr-checkbox" checked={korb.has(d.dropbox_pfad)}
                                      onChange={() => toggleExpandedFile(o.pfad, d)} onClick={e => e.stopPropagation()} />
                                  )}
                                  <span className="kompr-expand-fname" onClick={e => { e.stopPropagation(); openPreview(d); }}>
                                    {d.media_type === "video" ? "📹 " : ""}{d.name}
                                  </span>
                                  <span className="kompr-expand-fsize">{fmtSize(d.groesse)}</span>
                                  {d.ersparnis > 0 && <span className="kompr-expand-savings">~{fmtSize(d.ersparnis)}</span>}
                                </div>
                              ))}
                            </React.Fragment>
                          ));
                        };

                        return (
                          <div className="kompr-expand-content">
                            {/* Aktionen oben rechts + Select-All */}
                            <div className="kompr-expand-top-bar">
                              <div className="kompr-expand-stats">
                                {/* apple_skip-Dateien sind nicht auswählbar */}
                                {(() => { const selectable = allFiles.filter(d => d.status !== "apple_skip"); return (
                                <label className="kompr-expand-select-all" title="Alle Dateien auswählen">
                                  <input type="checkbox" className="kompr-checkbox"
                                    checked={selectable.length > 0 && selectable.every(d => korb.has(d.dropbox_pfad))}
                                    ref={el => { if (el) el.indeterminate = fdSel.length > 0 && !selectable.every(d => korb.has(d.dropbox_pfad)); }}
                                    onChange={() => {
                                      const allSel = selectable.every(d => korb.has(d.dropbox_pfad));
                                      korbSync(selectable, !allSel);
                                    }}
                                  />
                                  <span style={{ fontSize: "0.75rem" }}>Alle ({selectable.length})</span>
                                </label>); })()}
                                {komprimierbare.length > 0 && <span className="kompr-expand-stat kompr-expand-stat--kompr">{komprimierbare.length} komprimierbar</span>}
                                {umbenennbare.length > 0 && <span className="kompr-expand-stat kompr-expand-stat--umb">{umbenennbare.length} umbenennen</span>}
                                {erledigte.length > 0 && <span className="kompr-expand-stat kompr-expand-stat--done">{erledigte.length} erledigt</span>}
                                {originale.length > 0 && <span className="kompr-expand-stat kompr-expand-stat--orig">{originale.length} original</span>}
                              </div>
                              <div className="kompr-expand-actions-top">
                                {selKompr > 0 && (
                                  <button className="kompr-btn kompr-btn--start kompr-btn--sm" onClick={() => handleExpandedKomprimieren(o.pfad)} disabled={running}>
                                    {running ? "⏳" : `🗜️ ${selKompr}`}
                                  </button>
                                )}
                                {fdSel.length > 0 && (
                                  <button className="kompr-btn kompr-btn--rename kompr-btn--sm" onClick={() => handleExpandedRename(o.pfad)}>
                                    📝 {fdSel.length}
                                  </button>
                                )}
                                {fdSel.length > 0 && (
                                  <button className="kompr-btn kompr-btn--delete kompr-btn--sm" onClick={() => handleDeleteFiles(fdSel.map(d => d.dropbox_pfad))}>
                                    🗑️ {fdSel.length}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Sektion 1: Komprimierbar */}
                            {komprimierbare.length > 0 && (
                              <div className="kompr-expand-section">
                                <div className="kompr-expand-section-header kompr-expand-section--kompr">
                                  <input type="checkbox" className="kompr-checkbox"
                                    checked={komprimierbare.length > 0 && komprimierbare.every(d => korb.has(d.dropbox_pfad))}
                                    onChange={() => toggleExpandedAll(o.pfad, "komprimierbar")} />
                                  <span>Komprimierbar ({komprimierbare.length})</span>
                                  {selKompr > 0 && <span className="kompr-expand-sel-count">{selKompr} ausgewählt</span>}
                                  {selKompr > 0 && (
                                    <button className="kompr-btn kompr-btn--rename kompr-btn--xs" onClick={() => handleExpandedRename(o.pfad)}>
                                      📝 {selKompr}
                                    </button>
                                  )}
                                </div>
                                {renderSection(komprimierbare, "komprimierbar")}
                              </div>
                            )}

                            {/* Sektion 2: Umbenennen */}
                            {umbenennbare.length > 0 && (
                              <div className="kompr-expand-section">
                                <div className="kompr-expand-section-header kompr-expand-section--umb">
                                  <input type="checkbox" className="kompr-checkbox"
                                    checked={umbenennbare.length > 0 && umbenennbare.every(d => korb.has(d.dropbox_pfad))}
                                    onChange={() => toggleExpandedAll(o.pfad, "uebersprungen")} />
                                  <span>Umbenennen nötig ({umbenennbare.length})</span>
                                  {selUmb > 0 && <span className="kompr-expand-sel-count">{selUmb} ausgewählt</span>}
                                </div>
                                {renderSection(umbenennbare, "uebersprungen")}
                              </div>
                            )}

                            {/* Sektion 3: Erledigt */}
                            {erledigte.length > 0 && (
                              <div className="kompr-expand-section">
                                <div className="kompr-expand-section-header kompr-expand-section--done">
                                  <input type="checkbox" className="kompr-checkbox"
                                    checked={erledigte.length > 0 && erledigte.every(d => korb.has(d.dropbox_pfad))}
                                    onChange={() => toggleExpandedAll(o.pfad, "erledigt")} />
                                  <span>Erledigt ({erledigte.length})</span>
                                  {selErl > 0 && <span className="kompr-expand-sel-count">{selErl} ausgewählt</span>}
                                </div>
                                {renderSection(erledigte, "erledigt")}
                              </div>
                            )}

                            {/* Sektion 4: Original */}
                            {originale.length > 0 && (
                              <div className="kompr-expand-section">
                                <div className="kompr-expand-section-header kompr-expand-section--orig">
                                  <input type="checkbox" className="kompr-checkbox"
                                    checked={originale.length > 0 && originale.every(d => korb.has(d.dropbox_pfad))}
                                    onChange={() => toggleExpandedAll(o.pfad, "original")} />
                                  <span>Original / klein genug ({originale.length})</span>
                                  {selOrig > 0 && <span className="kompr-expand-sel-count">{selOrig} ausgewählt</span>}
                                </div>
                                {renderSection(originale, "original")}
                              </div>
                            )}

                            {/* Sektion 5: Apple-Format (HEIC/HEIF) – nicht komprimierbar */}
                            {appleSkip.length > 0 && (
                              <div className="kompr-expand-section">
                                <div className="kompr-expand-section-header kompr-expand-section--apple">
                                  <span>🍎 Apple-Format / HEIC ({appleSkip.length})</span>
                                  <span className="kompr-expand-section-hint">wird übersprungen</span>
                                </div>
                                {renderSection(appleSkip, "apple_skip")}
                              </div>
                            )}
                          </div>
                        );
                      })() : null}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Dateien */}
          {browseData.dateien.length > 0 && (
            <div className="kompr-files">
              <div className="kompr-section-label">
                Dateien
                <span className="kompr-section-count">{browseData.dateien.length}</span>
                {selectedFiles.size > 0 && (
                  <span className="kompr-section-selected">{selectedFiles.size} ausgewählt</span>
                )}
              </div>
              <table className="kompr-table">
                <thead>
                  <tr>
                    <th className="kompr-th-check">
                      <input
                        type="checkbox"
                        checked={browseData.dateien.length > 0 && selectedFiles.size >= browseData.dateien.length}
                        onChange={toggleAll}
                        className="kompr-checkbox"
                      />
                    </th>
                    <th>Datei</th>
                    <th>Größe</th>
                    <th>Status</th>
                    <th>Ersparnis</th>
                  </tr>
                </thead>
                <tbody>
                  {browseData.dateien.map((d, i) => (
                    <tr
                      key={i}
                      className={`kompr-file-row kompr-file--${d.status} ${selectedFiles.has(d.dropbox_pfad) ? "kompr-file--selected" : ""}`}
                      onClick={() => toggleFile(d)}
                    >
                      <td className="kompr-td-check">
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(d.dropbox_pfad)}
                          onChange={() => toggleFile(d)}
                          onClick={e => e.stopPropagation()}
                          className="kompr-checkbox"
                        />
                      </td>
                      <td className="kompr-td-name" title={d.name}>
                        <span className="kompr-name-link"
                          {...dragOutProps(d.dropbox_pfad, d.name)}
                          {...contextShareProps(d.dropbox_pfad, d.name)}
                          onClick={(e) => { e.stopPropagation(); openPreview(d); }}>
                          {d.media_type === "video" ? "📹 " : ""}{d.name}
                        </span>
                      </td>
                      <td className="kompr-td-size">{fmtSize(d.groesse)}</td>
                      <td>
                        {d.status === "komprimierbar" && <span className="kompr-badge">{d.media_type === "video" ? "🎬 komprimierbar" : "komprimierbar"}</span>}
                        {d.status === "erledigt" && <span className="kompr-badge kompr-badge--done">✓ erledigt</span>}
                        {d.status === "original" && <span className="kompr-badge kompr-badge--original">✓ original</span>}
                        {d.status === "uebersprungen" && <span className="kompr-badge kompr-badge--skip">übersprungen</span>}
                        {d.media_type === "video" && d.groesse > 500 * 1048576 && (
                          <span className="kompr-badge kompr-badge--warn" title="Sehr große Datei – dauert lange">⚠ {Math.round(d.groesse / 1048576)} MB</span>
                        )}
                      </td>
                      <td className="kompr-td-ersparnis">
                        {d.ersparnis > 0
                          ? <span className={`kompr-ersparnis-val ${d.status === "erledigt" ? "kompr-ersparnis--done" : ""}`}>
                              {d.status === "erledigt" ? "" : "~"}{fmtSize(d.ersparnis)}
                            </span>
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Leer */}
          {browseData.ordner.length === 0 && browseData.dateien.length === 0 && (
            <div className="kompr-empty">Ordner ist leer</div>
          )}

          {/* Aktionen */}
          <div className="kompr-actions">
            {(() => {
              const selKomprCount = browseData?.dateien?.filter(d => selectedFiles.has(d.dropbox_pfad) && d.status === "komprimierbar").length || 0;
              return selKomprCount > 0 && (
                <button
                  className="kompr-btn kompr-btn--start"
                  onClick={handleKomprimierenAuswahl}
                  disabled={running}
                >
                  {running ? "⏳ Läuft…" : `🗜️ ${selKomprCount} Dateien komprimieren`}
                </button>
              );
            })()}

            {/* Welle 63: immer verfügbar (auch im Root ohne direkte Bilder) —
                komprimiert rekursiv ab dem aktuellen Ordner, kein „Ordner für Ordner" mehr. */}
            {selectedFiles.size === 0 && (
              <button
                className="kompr-btn kompr-btn--start-all"
                onClick={() => handleKomprimieren(true)}
                disabled={running}
                title="Komprimiert alle Bilder ab dem aktuellen Ordner inkl. aller Unterordner"
              >
                {running ? "⏳ Läuft…" : `🚀 Alle hier + Unterordner komprimieren`}
              </button>
            )}

            {selectedFiles.size > 0 && (
              <button
                className="kompr-btn kompr-btn--delete"
                onClick={() => handleDeleteFiles([...selectedFiles])}
              >
                🗑️ {selectedFiles.size} löschen
              </button>
            )}
          </div>

          {/* Rekursiver Scan-Ergebnis */}
          {scanResult && (
            <div className="kompr-scan">
              <div className="kompr-scan-summary">
                <div className="kompr-summary-item">
                  <span className="kompr-summary-val">{scanResult.gesamt_komprimierbar}</span>
                  <span className="kompr-summary-label">komprimierbar</span>
                </div>
                <div className="kompr-summary-item">
                  <span className="kompr-summary-val">{scanResult.gesamt_groesse_mb} MB</span>
                  <span className="kompr-summary-label">Gesamtgröße</span>
                </div>
                <div className="kompr-summary-item">
                  <span className="kompr-summary-val">{scanResult.bereits_komprimiert}</span>
                  <span className="kompr-summary-label">bereits erledigt</span>
                </div>
                <div className="kompr-summary-item">
                  <span className="kompr-summary-val">{scanResult.uebersprungen}</span>
                  <span className="kompr-summary-label">übersprungen</span>
                </div>
              </div>

              {scanResult.ordner.length > 0 && (
                <table className="kompr-table">
                  <thead>
                    <tr>
                      <th>Ordner</th>
                      <th>Bilder</th>
                      <th>Größe</th>
                      <th>Komprimierbar</th>
                      <th>Erledigt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanResult.ordner.map((o, i) => (
                      <tr key={i}>
                        <td className="kompr-td-path">{o.ordner}</td>
                        <td>{o.bilder}</td>
                        <td>{fmtSize(o.groesse_bytes)}</td>
                        <td>{o.komprimierbar > 0 ? <span className="kompr-badge">{o.komprimierbar}</span> : "—"}</td>
                        <td>{o.bereits_komprimiert > 0 ? <span className="kompr-badge kompr-badge--done">{o.bereits_komprimiert}</span> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Ergebnis der Ausführung */}
      {result && (
        <div className="kompr-result">
          <h3>Ergebnis</h3>
          <div className="kompr-result-grid">
            <div className="kompr-result-item kompr-result--ok">
              <span className="kompr-result-val">{result.verarbeitet}</span>
              <span>verarbeitet</span>
            </div>
            <div className="kompr-result-item">
              <span className="kompr-result-val">{result.gespart_mb} MB</span>
              <span>gespart</span>
            </div>
            <div className="kompr-result-item">
              <span className="kompr-result-val">{result.uebersprungen}</span>
              <span>übersprungen</span>
            </div>
            {result.fehler > 0 && (
              <div className="kompr-result-item kompr-result--err">
                <span className="kompr-result-val">{result.fehler}</span>
                <span>Fehler</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Letzte Fehler – nur anzeigen wenn aktuell Fehler vorhanden */}
      {status?.fehler_count > 0 && status?.letzte_fehler?.length > 0 && (
        <div className="kompr-errors">
          <h3>Letzte Fehler</h3>
          <div className="kompr-error-list">
            {status.letzte_fehler.map((f, i) => (
              <div key={i} className="kompr-error-item">
                <span className="kompr-error-name">{f.foto_name}</span>
                <span className="kompr-error-msg">{f.fehler}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floating Viewer */}
      {(previewUrl || previewLoading) && (
        <div
          className="kompr-viewer-float"
          style={{ left: viewerPos.x, top: viewerPos.y }}
          onWheel={onViewerWheel}
        >
          <div className="kompr-viewer-header" onMouseDown={onViewerDragStart}>
            <span className="kompr-viewer-drag-hint">⠿</span>
            <span className="kompr-viewer-name" title={previewName}>
              {previewName}
            </span>
            <div className="kompr-viewer-zoom-controls">
              <button onClick={() => setZoom(z => Math.min(5, +(z + 0.25).toFixed(2)))} title="Vergrößern">＋</button>
              <span>{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom(z => Math.max(0.3, +(z - 0.25).toFixed(2)))} title="Verkleinern">－</button>
              <button onClick={() => setZoom(1)} title="Zurücksetzen">↺</button>
            </div>
            <button className="kompr-viewer-close" onMouseDown={e => e.stopPropagation()} onClick={() => { setPreviewUrl(null); setPreviewName(""); }}>×</button>
          </div>
          {previewSynologyPfad && (
            <div className="kompr-viewer-original-bar" onMouseDown={e => e.stopPropagation()}>
              {showingOriginal
                ? <button className="kompr-viewer-original-btn" onClick={() => setShowingOriginal(false)}>▲ Original ausblenden</button>
                : <button className="kompr-viewer-original-btn" onClick={openOriginal} disabled={originalLoading}>{originalLoading ? "Lädt…" : "📦 Original ansehen (NAS)"}</button>
              }
            </div>
          )}
          <div className="kompr-viewer-imgwrap">
            {previewLoading
              ? <div className="kompr-viewer-loading">Lädt…</div>
              : <>
                  <div className="kompr-viewer-section-label">Komprimiert</div>
                  <img
                    src={previewUrl}
                    alt={previewName}
                    className="kompr-viewer-img"
                    style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                  />
                  {showingOriginal && (
                    <>
                      <div className="kompr-viewer-section-divider">
                        <span className="kompr-viewer-section-label kompr-viewer-section-label--original">Original (NAS)</span>
                      </div>
                      {originalLoading
                        ? <div className="kompr-viewer-loading">Lädt Original…</div>
                        : originalUrl
                          ? <img
                              src={originalUrl}
                              alt={`${previewName} (Original)`}
                              className="kompr-viewer-img"
                              style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                            />
                          : null
                      }
                    </>
                  )}
                </>
            }
          </div>
        </div>
      )}

      {/* Confirm-Modal */}
      {showConfirm && (
        <div className="kompr-overlay" onClick={() => setShowConfirm(false)}>
          <div className="kompr-modal" onClick={e => e.stopPropagation()}>
            <div className="kompr-modal-icon">🗜️</div>
            <h3 className="kompr-modal-title">Komprimierung starten</h3>
            <div className="kompr-modal-body">
              <div className="kompr-modal-target">
                <span className="kompr-modal-label">Ordner</span>
                <span className="kompr-modal-value">{currentPath || `${modusLabel} (Root)`}</span>
              </div>
              {confirmRekursiv && (
                <div className="kompr-modal-hint">inkl. aller Unterordner</div>
              )}
              <div className="kompr-modal-steps">
                <div className="kompr-modal-step">
                  <span className="kompr-modal-step-icon">📦</span>
                  <span>Originale → <strong>Synology NAS</strong></span>
                </div>
                <div className="kompr-modal-step">
                  <span className="kompr-modal-step-icon">🗜️</span>
                  <span>Komprimiert → <strong>Dropbox</strong></span>
                </div>
              </div>
              {browseData?.stats?.komprimierbar > 0 && (
                <div className="kompr-modal-stats">
                  <span>{browseData.stats.komprimierbar} Dateien</span>
                  <span className="kompr-modal-stats-sep">·</span>
                  <span>~{fmtSize(browseData.stats.ersparnis_schaetzung)} Ersparnis</span>
                </div>
              )}
            </div>
            <div className="kompr-modal-actions">
              <button className="kompr-modal-btn kompr-modal-btn--cancel" onClick={() => setShowConfirm(false)}>
                Abbrechen
              </button>
              <button className="kompr-modal-btn kompr-modal-btn--confirm" onClick={doKomprimieren}>
                🚀 Starten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Umbenenn-Warnung */}
      {showUmbenennWarning && (
        <div className="kompr-overlay" onClick={() => setShowUmbenennWarning(null)}>
          <div className="kompr-modal" onClick={e => e.stopPropagation()}>
            <div className="kompr-modal-icon">⚠️</div>
            <h3 className="kompr-modal-title">Dateien müssen umbenannt werden</h3>
            <div className="kompr-modal-body">
              <p style={{ margin: "8px 0" }}>
                <strong>{showUmbenennWarning.count}</strong> der ausgewählten Dateien müssen noch umbenannt werden und können nicht komprimiert werden.
              </p>
              {showUmbenennWarning.komprimierbareCount > 0 && (
                <p style={{ margin: "8px 0" }}>
                  Es werden nur die <strong>{showUmbenennWarning.komprimierbareCount}</strong> komprimierbaren Dateien verarbeitet.
                </p>
              )}
            </div>
            <div className="kompr-modal-actions">
              <button className="kompr-modal-btn kompr-modal-btn--cancel" onClick={() => setShowUmbenennWarning(null)}>
                Abbrechen
              </button>
              {showUmbenennWarning.komprimierbareCount > 0 && (
                <button className="kompr-modal-btn kompr-modal-btn--confirm" onClick={showUmbenennWarning.onConfirm}>
                  🗜️ Trotzdem komprimieren
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lösch-Bestätigung */}
      {showDeleteConfirm && (
        <div className="kompr-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="kompr-modal" onClick={e => e.stopPropagation()}>
            <div className="kompr-modal-icon">🗑️</div>
            <h3 className="kompr-modal-title">
              {showDeleteConfirm.isFolder ? "Ordner löschen?" : `${showDeleteConfirm.pfade.length} Datei(en) löschen?`}
            </h3>
            <div className="kompr-modal-body">
              {showDeleteConfirm.isFolder ? (
                <>
                  <p style={{ margin: "8px 0" }}><strong>{showDeleteConfirm.ordnerName}</strong></p>
                  {showDeleteConfirm.hatInhalt && (
                    <p style={{ margin: "8px 0", color: "#ff6b6b" }}>
                      ⚠️ Ordner enthält noch <strong>{showDeleteConfirm.anzahlDateien}</strong> Datei(en)!
                    </p>
                  )}
                </>
              ) : (
                <p style={{ margin: "8px 0" }}>
                  {showDeleteConfirm.pfade.length === 1
                    ? showDeleteConfirm.pfade[0].split("/").pop()
                    : `${showDeleteConfirm.pfade.length} Dateien`}
                </p>
              )}
              <p style={{ margin: "8px 0", fontSize: "0.85em", opacity: 0.7 }}>
                Auf Dropbox dauerhaft löschen. Kann nicht rückgängig gemacht werden.
              </p>
            </div>
            <div className="kompr-modal-actions">
              <button className="kompr-modal-btn kompr-modal-btn--cancel" onClick={() => setShowDeleteConfirm(null)}>
                Abbrechen
              </button>
              <button className="kompr-modal-btn kompr-modal-btn--delete" onClick={doLoeschen}>
                🗑️ Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auswahl-Korb: sticky Aktionsleiste — sammelt Dateien über Ordnergrenzen */}
      {korb.size > 0 && (
        <div className="kompr-korb-bar">
          <span className="kompr-korb-info">
            <strong>{korb.size}</strong> Datei{korb.size !== 1 ? "en" : ""} aus{" "}
            <strong>{korbOrdner.size}</strong> Ordner{korbOrdner.size !== 1 ? "n" : ""} ausgewählt
          </span>
          <div className="kompr-korb-actions">
            <button
              className="kompr-btn kompr-btn--rename kompr-korb-main"
              onClick={handleKorbRename}
              disabled={running}
              title="Ausgewählte Dateien umbenennen — sie bleiben in ihrem Ordner und werden dabei komprimiert (Original auf Synology)"
            >
              ✎ Umbenennen + Komprimieren ({korb.size})
            </button>
            {korbKomprimierbar.length > 0 && (
              <button
                className="kompr-btn kompr-btn--start kompr-btn--sm"
                onClick={handleKorbKomprimieren}
                disabled={running}
                title="Nur komprimieren, ohne Umbenennen"
              >
                🗜️ Nur komprimieren ({korbKomprimierbar.length})
              </button>
            )}
            <button className="kompr-btn kompr-btn--sm kompr-korb-clear" onClick={korbClear} disabled={running}>
              Auswahl leeren
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
